import { SearchIndex } from '../../hooks/use-search';
import { TSearchResultSection } from './types';
import TbIndentIncrease from '../../components/icons/tabler/TbIndentIncrease';
import TbBooks from '../../components/icons/tabler/TbBooks';

import FlexSearch from 'flexsearch';

/**
 * Searches the docs for the given query.
 * @param query  The query to search for
 * @returns  The search results
 */
export function searchDocs(
  query: string,
  searchIndex: SearchIndex
): TSearchResultSection[] {
  // Initialize FlexSearch index
  const index = new FlexSearch.Index({
    tokenize: 'full',
    limit: 5
  });

  // Add documents to the index
  Object.entries(searchIndex).forEach(([path, document]) => {
    index.add(path, document.title);
    // You can add more fields for searching here if needed

    Object.entries(document.data).forEach(([key, value]) => {
      const dataTile = key.split('#')[1] || key;

      if (dataTile) {
        index.add(`${path}____${key}____title`, dataTile);
      }

      index.add(`${path}____${key}____data`, value);
    });
  });

  // Perform searchs
  const results = index.search({
    query,
    limit: 5
  });

  const searchResults: {
    [path: string]: {
      document: {
        id: string;
        title: string;
        data: Record<string, string>;
      };
    };
  } = {};

  for (const id of results) {
    const [path, dataKey, dataType] = id.toString().split('____');

    const document = searchIndex[path];

    if (!document) {
      continue;
    }

    // Find matched part of the title
    const matchedTitle = document.title;

    // Find matched data keys and their respective matched parts

    let dataTitle = null;

    if (dataType === 'title') {
      // split at first # and get the second part (also when there are multiple #)
      const title = dataKey.split('#').slice(1).join('#') || dataKey;

      dataTitle = title;
    }

    const dataMatch = getRangeAroundMatch(document.data[dataKey], query);

    const searchResult = searchResults[path];

    if (searchResult) {
      if (dataKey) {
        if (!searchResult.document.data[dataKey]) {
          searchResult.document.data[dataKey] = document.data[dataKey];
        }
      }

      searchResult.document.title = matchedTitle || document.title;
    } else {
      searchResults[path] = {
        document: {
          id: document.id,
          title: matchedTitle || document.title,
          data: {}
        }
      };

      if (dataKey) {
        searchResults[path].document.data[dataKey] = document.data[dataKey];
      }
    }
  }

  function getRangeAroundMatch(str: string, query: string) {
    if (!str) return null;

    const index = str.toLowerCase().indexOf(query.toLowerCase());
    const start = Math.max(0, index - 20);
    const end = Math.min(str.length, index + query.length + 20);

    return str.slice(start, end);
  }

  // Return the search results
  return Object.entries(searchResults).map(([path, result]) => {
    return {
      title: result.document.title,
      results: Object.entries(result.document.data).map(([key, value]) => {
        const slug = key.split('#')[0];
        const title = key.split('#')[1] || key;

        return {
          description: value,
          to: slug ? `${path}#${slug}` : path,
          title: title
        };
      }),
      icon: <TbBooks />,
      resultIcon: <TbIndentIncrease />
    };
  });
}

export function getDefaultSearchDocs(
  data: SearchIndex,
  docsRoot: string = '/docs/'
): TSearchResultSection[] {
  const results: TSearchResultSection[] = [];
  Object.keys(data).forEach(key => {
    if (!key.startsWith(docsRoot) || key === docsRoot) return;
    const item = data[key];
    const summary = Object.keys(item.data)
      .find(key => key.length > 0 && item.data[key].length > 0)
      ?.slice(0, 100);
    results.push({
      title: item.title,
      results: [
        {
          description: summary ?? item.title ?? '',
          to: key,
          title: item.title ?? ''
        }
      ],
      resultIcon: <TbIndentIncrease />
    });
  });
  return results;
}
